import { useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight, ArrowUp, RotateCcw } from 'lucide-react'

// 游戏常量
const CANVAS_WIDTH = 800
const CANVAS_HEIGHT = 400
const GRAVITY = 0.6
const JUMP_FORCE = -12
const MOVE_SPEED = 5

// 游戏对象类型
interface Player {
  x: number
  y: number
  width: number
  height: number
  vx: number
  vy: number
  jumping: boolean
  color: string
}

interface Platform {
  x: number
  y: number
  width: number
  height: number
  color: string
}

interface Enemy {
  x: number
  y: number
  width: number
  height: number
  vx: number
  color: string
}

interface Coin {
  x: number
  y: number
  width: number
  height: number
  collected: boolean
}

export default function Game() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [score, setScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [gameWon, setGameWon] = useState(false)
  const [keys, setKeys] = useState<{ [key: string]: boolean }>({})

  // 游戏状态引用
  const gameState = useRef({
    player: {
      x: 50,
      y: 300,
      width: 30,
      height: 30,
      vx: 0,
      vy: 0,
      jumping: false,
      color: '#ff4444'
    } as Player,
    platforms: [
      { x: 0, y: 370, width: 800, height: 30, color: '#8B4513' }, // 地面
      { x: 200, y: 300, width: 100, height: 20, color: '#228B22' },
      { x: 400, y: 250, width: 100, height: 20, color: '#228B22' },
      { x: 600, y: 200, width: 100, height: 20, color: '#228B22' },
      { x: 100, y: 200, width: 80, height: 20, color: '#228B22' },
      { x: 300, y: 150, width: 80, height: 20, color: '#228B22' },
      { x: 500, y: 100, width: 100, height: 20, color: '#228B22' },
    ] as Platform[],
    enemies: [
      { x: 300, y: 340, width: 25, height: 25, vx: 2, color: '#8B0000' },
      { x: 500, y: 340, width: 25, height: 25, vx: -2, color: '#8B0000' },
      { x: 650, y: 175, width: 25, height: 25, vx: 1.5, color: '#8B0000' },
    ] as Enemy[],
    coins: [
      { x: 240, y: 260, width: 20, height: 20, collected: false },
      { x: 440, y: 210, width: 20, height: 20, collected: false },
      { x: 640, y: 160, width: 20, height: 20, collected: false },
      { x: 140, y: 160, width: 20, height: 20, collected: false },
      { x: 340, y: 110, width: 20, height: 20, collected: false },
      { x: 540, y: 60, width: 20, height: 20, collected: false },
    ] as Coin[],
    camera: { x: 0 }
  })

  // 键盘事件监听
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      setKeys(prev => ({ ...prev, [e.key]: true }))
      // 防止方向键滚动页面
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault()
      }
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      setKeys(prev => ({ ...prev, [e.key]: false }))
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  // 碰撞检测
  const checkCollision = (rect1: any, rect2: any) => {
    return (
      rect1.x < rect2.x + rect2.width &&
      rect1.x + rect1.width > rect2.x &&
      rect1.y < rect2.y + rect2.height &&
      rect1.y + rect1.height > rect2.y
    )
  }

  // 重置游戏
  const resetGame = () => {
    gameState.current = {
      player: {
        x: 50,
        y: 300,
        width: 30,
        height: 30,
        vx: 0,
        vy: 0,
        jumping: false,
        color: '#ff4444'
      },
      platforms: [
        { x: 0, y: 370, width: 800, height: 30, color: '#8B4513' },
        { x: 200, y: 300, width: 100, height: 20, color: '#228B22' },
        { x: 400, y: 250, width: 100, height: 20, color: '#228B22' },
        { x: 600, y: 200, width: 100, height: 20, color: '#228B22' },
        { x: 100, y: 200, width: 80, height: 20, color: '#228B22' },
        { x: 300, y: 150, width: 80, height: 20, color: '#228B22' },
        { x: 500, y: 100, width: 100, height: 20, color: '#228B22' },
      ],
      enemies: [
        { x: 300, y: 340, width: 25, height: 25, vx: 2, color: '#8B0000' },
        { x: 500, y: 340, width: 25, height: 25, vx: -2, color: '#8B0000' },
        { x: 650, y: 175, width: 25, height: 25, vx: 1.5, color: '#8B0000' },
      ],
      coins: [
        { x: 240, y: 260, width: 20, height: 20, collected: false },
        { x: 440, y: 210, width: 20, height: 20, collected: false },
        { x: 640, y: 160, width: 20, height: 20, collected: false },
        { x: 140, y: 160, width: 20, height: 20, collected: false },
        { x: 340, y: 110, width: 20, height: 20, collected: false },
        { x: 540, y: 60, width: 20, height: 20, collected: false },
      ],
      camera: { x: 0 }
    }
    setScore(0)
    setGameOver(false)
    setGameWon(false)
  }

  // 游戏循环
  useEffect(() => {
    if (gameOver || gameWon) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let animationId: number

    const gameLoop = () => {
      const state = gameState.current
      const player = state.player

      // 玩家移动控制
      if (keys['ArrowLeft'] || keys['a']) {
        player.vx = -MOVE_SPEED
      } else if (keys['ArrowRight'] || keys['d']) {
        player.vx = MOVE_SPEED
      } else {
        player.vx = 0
      }

      // 跳跃
      if ((keys['ArrowUp'] || keys['w'] || keys[' ']) && !player.jumping) {
        player.vy = JUMP_FORCE
        player.jumping = true
      }

      // 应用重力
      player.vy += GRAVITY

      // 更新玩家位置
      player.x += player.vx
      player.y += player.vy

      // 平台碰撞检测
      player.jumping = true
      for (const platform of state.platforms) {
        if (checkCollision(player, platform)) {
          // 从上方落下
          if (player.vy > 0 && player.y < platform.y) {
            player.y = platform.y - player.height
            player.vy = 0
            player.jumping = false
          }
          // 从下方跳上去
          else if (player.vy < 0 && player.y > platform.y) {
            player.y = platform.y + platform.height
            player.vy = 0
          }
        }
      }

      // 边界检测
      if (player.x < 0) player.x = 0
      if (player.y > CANVAS_HEIGHT) {
        setGameOver(true)
      }

      // 敌人移动和碰撞
      for (const enemy of state.enemies) {
        enemy.x += enemy.vx

        // 敌人巡逻
        if (enemy.x > 750 || enemy.x < 50) {
          enemy.vx *= -1
        }

        // 敌人碰撞检测
        if (checkCollision(player, enemy)) {
          // 踩踏敌人
          if (player.vy > 0 && player.y < enemy.y) {
            // 移除敌人
            state.enemies = state.enemies.filter(e => e !== enemy)
            player.vy = -8 // 小跳跃
            setScore(s => s + 100)
          } else {
            setGameOver(true)
          }
        }
      }

      // 金币收集
      for (const coin of state.coins) {
        if (!coin.collected && checkCollision(player, coin)) {
          coin.collected = true
          setScore(s => s + 50)
        }
      }

      // 检查是否收集所有金币
      if (state.coins.every(c => c.collected)) {
        setGameWon(true)
      }

      // 相机跟随
      state.camera.x = player.x - CANVAS_WIDTH / 3
      if (state.camera.x < 0) state.camera.x = 0
      if (state.camera.x > 200) state.camera.x = 200

      // 清空画布
      ctx.fillStyle = '#87CEEB' // 天空蓝
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

      // 绘制云朵背景
      ctx.fillStyle = '#FFFFFF'
      ctx.beginPath()
      ctx.arc(100 - state.camera.x * 0.3, 80, 30, 0, Math.PI * 2)
      ctx.arc(130 - state.camera.x * 0.3, 80, 35, 0, Math.PI * 2)
      ctx.arc(160 - state.camera.x * 0.3, 80, 30, 0, Math.PI * 2)
      ctx.fill()

      ctx.beginPath()
      ctx.arc(400 - state.camera.x * 0.3, 60, 25, 0, Math.PI * 2)
      ctx.arc(425 - state.camera.x * 0.3, 60, 30, 0, Math.PI * 2)
      ctx.arc(450 - state.camera.x * 0.3, 60, 25, 0, Math.PI * 2)
      ctx.fill()

      // 绘制平台
      for (const platform of state.platforms) {
        ctx.fillStyle = platform.color
        ctx.fillRect(
          platform.x - state.camera.x,
          platform.y,
          platform.width,
          platform.height
        )
        // 平台纹理
        ctx.fillStyle = 'rgba(0,0,0,0.2)'
        ctx.fillRect(
          platform.x - state.camera.x,
          platform.y + platform.height - 5,
          platform.width,
          5
        )
      }

      // 绘制金币
      for (const coin of state.coins) {
        if (!coin.collected) {
          ctx.fillStyle = '#FFD700'
          ctx.beginPath()
          ctx.arc(
            coin.x + coin.width / 2 - state.camera.x,
            coin.y + coin.height / 2,
            coin.width / 2,
            0,
            Math.PI * 2
          )
          ctx.fill()
          // 金币高光
          ctx.fillStyle = '#FFED4A'
          ctx.beginPath()
          ctx.arc(
            coin.x + coin.width / 2 - 3 - state.camera.x,
            coin.y + coin.height / 2 - 3,
            3,
            0,
            Math.PI * 2
          )
          ctx.fill()
        }
      }

      // 绘制敌人
      for (const enemy of state.enemies) {
        ctx.fillStyle = enemy.color
        ctx.fillRect(
          enemy.x - state.camera.x,
          enemy.y,
          enemy.width,
          enemy.height
        )
        // 眼睛
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(enemy.x - state.camera.x + 5, enemy.y + 5, 6, 6)
        ctx.fillRect(enemy.x - state.camera.x + 14, enemy.y + 5, 6, 6)
        ctx.fillStyle = '#000000'
        ctx.fillRect(enemy.x - state.camera.x + 7, enemy.y + 7, 2, 2)
        ctx.fillRect(enemy.x - state.camera.x + 16, enemy.y + 7, 2, 2)
      }

      // 绘制玩家（马里奥风格）
      const pX = player.x - state.camera.x
      const pY = player.y

      // 身体
      ctx.fillStyle = player.color
      ctx.fillRect(pX, pY, player.width, player.height)
      
      // 帽子
      ctx.fillStyle = '#FF0000'
      ctx.fillRect(pX, pY, player.width, 8)
      
      // 脸部
      ctx.fillStyle = '#FFDAB9'
      ctx.fillRect(pX + 2, pY + 8, player.width - 4, 10)
      
      // 眼睛
      ctx.fillStyle = '#000000'
      if (player.vx >= 0) {
        ctx.fillRect(pX + 18, pY + 10, 3, 3)
      } else {
        ctx.fillRect(pX + 9, pY + 10, 3, 3)
      }
      
      // 背带裤
      ctx.fillStyle = '#0000FF'
      ctx.fillRect(pX + 5, pY + 20, player.width - 10, 10)

      // 绘制终点旗帜
      ctx.fillStyle = '#FFD700'
      ctx.fillRect(750 - state.camera.x, 70, 5, 100)
      ctx.fillStyle = '#FF0000'
      ctx.beginPath()
      ctx.moveTo(755 - state.camera.x, 70)
      ctx.lineTo(800 - state.camera.x, 90)
      ctx.lineTo(755 - state.camera.x, 110)
      ctx.fill()

      animationId = requestAnimationFrame(gameLoop)
    }

    gameLoop()

    return () => {
      cancelAnimationFrame(animationId)
    }
  }, [keys, gameOver, gameWon])

  return (
    <div className="animate-fade-in px-4 pt-4">
      {/* 游戏标题 */}
      <div className="text-center mb-4">
        <h2 className="text-2xl font-bold text-telegram-text mb-2">
          🎮 超级玛丽
        </h2>
        <p className="text-telegram-secondary text-sm">
          收集所有金币并到达终点旗帜！
        </p>
      </div>

      {/* 分数显示 */}
      <div className="flex justify-between items-center mb-4 px-4 py-2 bg-telegram-dark rounded-xl">
        <div className="text-telegram-text font-semibold">
          分数: <span className="text-yellow-400">{score}</span>
        </div>
        <button
          onClick={resetGame}
          className="flex items-center space-x-1 px-3 py-1.5 bg-telegram-blue rounded-lg text-white text-sm hover:bg-telegram-blue/80 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          <span>重新开始</span>
        </button>
      </div>

      {/* 游戏画布 */}
      <div className="relative flex justify-center">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="rounded-xl border-2 border-telegram-secondary/20 max-w-full"
          style={{ 
            imageRendering: 'pixelated',
            backgroundColor: '#87CEEB'
          }}
        />

        {/* 游戏结束遮罩 */}
        {(gameOver || gameWon) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl">
            <div className="text-center p-6 bg-telegram-dark rounded-2xl">
              <h3 className={`text-3xl font-bold mb-4 ${gameWon ? 'text-green-400' : 'text-red-400'}`}>
                {gameWon ? '🎉 恭喜通关！' : '💥 游戏结束'}
              </h3>
              <p className="text-telegram-text mb-2">
                最终分数: <span className="text-yellow-400 text-xl">{score}</span>
              </p>
              <button
                onClick={resetGame}
                className="mt-4 px-6 py-2 bg-telegram-blue rounded-lg text-white font-medium hover:bg-telegram-blue/80 transition-colors"
              >
                再玩一次
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 操作说明 */}
      <div className="mt-4 px-4 py-3 bg-telegram-dark rounded-xl">
        <h4 className="text-telegram-text font-medium mb-2">🎮 操作说明</h4>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center space-x-2 text-telegram-secondary">
            <div className="flex space-x-1">
              <ArrowLeft className="w-4 h-4" />
              <ArrowRight className="w-4 h-4" />
            </div>
            <span>左右移动</span>
          </div>
          <div className="flex items-center space-x-2 text-telegram-secondary">
            <div className="flex items-center">
              <ArrowUp className="w-4 h-4" />
              <span className="mx-1">/</span>
              <span>空格</span>
            </div>
            <span>跳跃</span>
          </div>
        </div>
        <p className="text-telegram-secondary text-xs mt-2">
          💡 提示：踩踏敌人可以消灭它们，收集所有金币后到达终点旗帜即可获胜！
        </p>
      </div>
    </div>
  )
}
